import React, { createContext, useCallback, useEffect, useMemo } from 'react'

import { useFindAppRouteMatch } from '../../../hooks/routes/use_find_app_route_match'
import { useAppLocationStateManager } from '../../../hooks/use_app_location_state_manager'
import { getPreloadedAppLocation } from '../../../reduxes/slices/app_location/selectors'
import {
  getCurrentHandle,
  getCurrentMemberId,
} from '../../../reduxes/slices/current/selectors'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { getMemberIds } from '../../../reduxes/slices/members/selectors'
import { getRoutesWithoutPrefill } from '../../../reduxes/slices/routes/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import {
  AppLocation,
  AppLocationContextType,
  AppLocationFragment,
  MENU_TAB,
  TOPIC_VIEW,
  emptyObject,
} from './constants'

// excludes memberId which is included separately at runtime
export const BLANK_APP_LOCATION: AppLocationFragment = {
  url: null,
  menuTab: null,
  topicView: null,
  topicId: null,
  directChatHandle: null,
  widgetView: null,
  welcomeMessageId: null,
  videoCollectionId: null,
  widgetQuestionId: null,
  productURL: null,
}

export const AppLocationContext = createContext<AppLocationContextType>({
  appLocation: emptyObject,
  prevAppLocation: emptyObject,
  setAppLocation: null,
  appLocationCache: emptyObject,
  goBack: undefined,
  setAppLocationCache: undefined,
  ready: false,
})

export const DEFAULT_APP_LOCATION_FRAGMENT: AppLocationFragment = {
  menuTab: MENU_TAB.LATEST,
  topicView: TOPIC_VIEW.CHAT_VIEW,
}

export const AppLocationProvider = ({
  children,
  dispatches,
}: {
  children: React.ReactElement
  dispatches: any
}) => {
  // preload the app location set in redux by the server
  const preloadedAppLocation = useSelectorDelegateStore(getPreloadedAppLocation)
  const {
    appLocation,
    appLocationCache,
    prevAppLocation,
    setAppLocationCache,
  } = useAppLocationStateManager(dispatches)
  const currentMemberId = useSelectorDelegateStore(getCurrentMemberId)
  const currentHandle = useSelectorDelegateStore(getCurrentHandle)
  const routes: any = useSelectorDelegateStore(getRoutesWithoutPrefill)
  const findAppRouteMatch = useFindAppRouteMatch()
  const memberIds = useSelectorDelegateStore(getMemberIds)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  useEffect(() => {
    const prevLocationForCurrentMember =
      appLocationCache.cache[preloadedAppLocation.memberId]
    const location = {
      ...BLANK_APP_LOCATION,
      ...DEFAULT_APP_LOCATION_FRAGMENT,
      ...prevLocationForCurrentMember, // to preserve location state such as topicView, e.g. refreshing when on the calls video view
      ...preloadedAppLocation,
      topicView: TOPIC_VIEW.CHAT_VIEW,
      ...(liveChatApp &&
        prevLocationForCurrentMember?.welcomeMessageId && {
          welcomeMessageId: prevLocationForCurrentMember.welcomeMessageId,
        }),
      ...(liveChatApp &&
        prevLocationForCurrentMember?.widgetQuestionId && {
          widgetQuestionId: prevLocationForCurrentMember.widgetQuestionId,
        }),
    }
    if (!location.widgetQuestionId) {
      location.widgetQuestionId = 'root'
    }
    setAppLocation(location, { initialize: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // responsible for preprocessing the location to always have a memberId and to be in the correct format
  const preprocessLocationChanges = useCallback(
    (currentMemberId, location, initialize) => {
      const locationChanges: { [key: string]: any } = {
        ...(initialize ? BLANK_APP_LOCATION : {}),
        memberId: currentMemberId,
        ...location,
      }
      Object.keys(locationChanges).forEach((key) => {
        if (locationChanges[key] === undefined) {
          locationChanges[key] = null
        }
        const value = locationChanges[key]
        switch (key) {
          case 'memberId':
            {
              if (value !== null) {
                locationChanges[key] = parseInt(value, 10)
              }
            }
            break
          case 'topicId': {
            if (value !== null) {
              locationChanges[key] = parseInt(value, 10)
            }
          }
        }
      })
      return locationChanges
    },
    [],
  )

  const setAppLocation = useCallback(
    (
      location: AppLocationFragment,
      options?: {
        initialize?: boolean
      },
    ) => {
      const { initialize } = options || {
        initialize: false,
      }
      if (!initialize && !Object.keys(appLocation).length) {
        // to prevent this warning, wrap the setAppLocation call in question in an if statement that checks if the ready attribute is true
        // eslint-disable-next-line no-console
        console.warn('app location state has not been initialized')
        return false
      }
      let locationChanges = preprocessLocationChanges(
        currentMemberId,
        location,
        initialize,
      )
      // Restore where the user last left off on page load. Only if they are visiting the base route path.
      if (initialize) {
        const routeMatch = findAppRouteMatch(locationChanges.url)
        const isOnBaseRoutePath =
          routeMatch?.pattern?.path === routes.admin_app_root.path
        const validMemberId = memberIds.includes(
          appLocationCache.lastSetMemberId,
        )
        if (
          isOnBaseRoutePath &&
          appLocationCache.lastSetMemberId &&
          validMemberId
        ) {
          locationChanges =
            appLocationCache.cache[appLocationCache.lastSetMemberId]
        }
      }

      const { memberId } = locationChanges
      // it is important to keep this set state pure; derived only from the prevState and without any side effects
      setAppLocationCache((prevState) => {
        const prevLocation = {
          ...BLANK_APP_LOCATION,
          ...(initialize ? DEFAULT_APP_LOCATION_FRAGMENT : {}),
          ...(prevState.lastSetMemberId
            ? prevState.cache[prevState.lastSetMemberId]
            : {}),
        } as AppLocation

        const newLocation = {
          ...prevLocation,
          ...locationChanges,
          ...(locationChanges.welcomeMessageId || !liveChatApp
            ? {}
            : { welcomeMessageId: prevLocation.welcomeMessageId }),
          ...(locationChanges.widgetQuestionId || !liveChatApp
            ? {}
            : { widgetQuestionId: prevLocation.widgetQuestionId || 'root' }),
        }
        return {
          ...prevState,
          lastSetMemberId: memberId,
          cache: {
            ...prevState.cache,
            [memberId]: newLocation,
          },
        }
      })

      return true
    },
    [
      appLocation,
      appLocationCache.cache,
      appLocationCache.lastSetMemberId,
      currentMemberId,
      findAppRouteMatch,
      liveChatApp,
      memberIds,
      preprocessLocationChanges,
      routes.admin_app_root.path,
      setAppLocationCache,
    ],
  )

  const setAppLocationCacheForMember = useCallback(
    (location: AppLocationFragment) => {
      setAppLocationCache((prevState) => {
        const newLocation = {
          memberId: currentMemberId,
          ...location,
        }
        const mId = newLocation.memberId
        return {
          ...prevState,
          cache: {
            ...prevState.cache,
            [mId]: {
              ...BLANK_APP_LOCATION,
              ...prevState.cache[mId],
              ...newLocation,
            },
          },
        }
      })
    },
    [currentMemberId, setAppLocationCache],
  )

  const goBack = useCallback(() => {
    if (Object.keys(prevAppLocation).length) {
      setAppLocation(prevAppLocation)
    } else {
      setAppLocation({
        ...BLANK_APP_LOCATION,
        ...DEFAULT_APP_LOCATION_FRAGMENT,
        url: routes.admin_app_root.path.replace(
          ':current_handle',
          currentHandle,
        ),
        menuTab: MENU_TAB.LATEST,
      })
    }
  }, [
    currentHandle,
    prevAppLocation,
    routes.admin_app_root.path,
    setAppLocation,
  ])

  const contextValue = useMemo(() => {
    return {
      appLocation,
      prevAppLocation,
      appLocationCache: appLocationCache.cache,
      goBack,
      setAppLocation,
      setAppLocationCache: setAppLocationCacheForMember,
      ready: !!Object.keys(appLocation).length,
    }
  }, [
    appLocation,
    prevAppLocation,
    setAppLocation,
    appLocationCache,
    goBack,
    setAppLocationCacheForMember,
  ])

  return (
    <AppLocationContext.Provider value={contextValue}>
      {children}
    </AppLocationContext.Provider>
  )
}
